import { useTranslation } from 'react-i18next';

import { formatLocale, useTranslationLocales } from '~/hooks/useTranslationLocales';

import type { LanguageApprovalOption } from '../../hooks/use-language-approval';

interface Props {
  options: LanguageApprovalOption[];
  selected: string[];
}

/**
 * Read-only mirror of the Decision step's `LanguageApprovalSection` — which
 * languages will ship, for reference while reviewing Preview. Editing the
 * approved-locale selection still only happens on Decision. Renders nothing
 * when the run has no translations (`options` is already empty in that case
 * — `useLanguageApproval` zeroes it out for a Meta batch or a user without
 * translation-view permission too, so no separate gate is needed here).
 */
export function DeliveryWizardLanguageSummary({ options, selected }: Props) {
  const { t } = useTranslation('common');
  const locales = useTranslationLocales(options.length > 0);
  if (options.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 text-sm"
      data-testid="delivery-wizard-language-summary"
    >
      <span className="text-muted-foreground">{t('deliveryWizard.preview.languages.label')}</span>
      <span
        className="bg-background rounded-md border px-2.5 py-1 text-xs font-medium"
        data-testid="delivery-wizard-language-badge-en-US"
      >
        {formatLocale('en-US', locales.data)}
      </span>
      {selected.map((locale) => (
        <span
          key={locale}
          className="bg-background rounded-md border px-2.5 py-1 text-xs font-medium"
          data-testid={`delivery-wizard-language-badge-${locale}`}
        >
          {formatLocale(locale, locales.data)}
        </span>
      ))}
    </div>
  );
}
